<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <button type="button" class="btn btn-sm btn-outline-secondary mb-2" @click="goBack">
                  <i class="fas fa-arrow-left me-1"></i> Volver a remisiones
                </button>
                <h6 class="mb-0">
                  Picking / Packing — Pedido #{{ remissionId }}
                </h6>
                <span class="text-xs text-secondary">
                  {{ remission?.name || 'Cargando...' }}
                  <span v-if="sellerLabel"> · Vendedor: {{ sellerLabel }}</span>
                </span>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span class="badge badge-sm" :class="remissionStatusClass(remission?.status)">
                  {{ remissionStatusLabel(remission?.status) }}
                </span>
                <span class="badge badge-sm" :class="orderStatusClass(remission?.order_status)">
                  {{ orderStatusLabel(remission?.order_status) }}
                </span>
                <button
                  v-if="conenviosEnabled && allComplete && !waybillId"
                  type="button"
                  class="btn btn-sm btn-success mb-0"
                  @click="openShippingModal"
                >
                  <i class="fas fa-barcode me-1"></i> Ticket de envío
                </button>
                <button
                  v-if="waybillId"
                  type="button"
                  class="btn btn-sm btn-primary mb-0"
                  :disabled="printingLabel"
                  @click="printLabel"
                >
                  <i class="fas" :class="printingLabel ? 'fa-spinner fa-spin' : 'fa-print'"></i>
                  Imprimir rótulo
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger mb-0"
                  :disabled="loading || pickedUnits === 0"
                  @click="resetAll"
                >
                  <i class="fas fa-undo me-1"></i> Reiniciar picking
                </button>
              </div>
            </div>
          </div>

          <div class="card-body">
            <!-- Barra de progreso -->
            <div class="mb-1 d-flex justify-content-between align-items-center">
              <span class="text-sm font-weight-bold">
                Progreso de preparación: {{ pickedUnits }} / {{ totalUnits }} unidades
              </span>
              <span class="text-sm font-weight-bold" :class="allComplete ? 'text-success' : 'text-secondary'">
                {{ progressPercent }}%
              </span>
            </div>
            <div class="progress" style="height: 20px;">
              <div
                class="progress-bar"
                :class="allComplete ? 'bg-success' : 'bg-info progress-bar-striped progress-bar-animated'"
                role="progressbar"
                :style="{ width: progressPercent + '%' }"
                :aria-valuenow="progressPercent"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>

            <div v-if="allComplete" class="alert alert-success text-white mt-3 mb-0 d-flex align-items-center">
              <i class="fas fa-check-circle me-2"></i>
              ¡Pedido completo! Todos los productos fueron preparados.
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h6 class="mb-0">Productos del pedido</h6>
            <p class="text-xs text-secondary mb-2">
              Escanea el código de barras (o digita la referencia y presiona Enter) para marcar unidades como preparadas.
            </p>
          </div>

          <div class="card-body">
            <!-- Escáner -->
            <div class="row mb-3">
              <div class="col-12 col-md-8 col-lg-6">
                <div class="input-group input-group-lg">
                  <span class="input-group-text"><i class="fas fa-barcode"></i></span>
                  <input
                    ref="scanInput"
                    v-model="scanCode"
                    type="text"
                    class="form-control"
                    :class="scanFlash.type === 'error' ? 'border-danger' : ''"
                    placeholder="Código de barras o referencia..."
                    autocomplete="off"
                    autofocus
                    :disabled="loading || allComplete"
                    @keyup.enter="processScan"
                    @blur="keepScannerFocus"
                  />
                  <button type="button" class="btn btn-primary mb-0" :disabled="loading || !scanCode.trim()" @click="processScan">
                    <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Resultado del último escaneo -->
            <div
              v-if="scanFlash.message"
              class="alert text-white d-flex align-items-center py-2"
              :class="scanFlash.type === 'success' ? 'bg-success' : scanFlash.type === 'warning' ? 'bg-warning' : 'bg-danger'"
            >
              <i :class="scanFlashIcon" class="me-2"></i>
              <span class="text-sm font-weight-bold">{{ scanFlash.message }}</span>
            </div>

            <div v-if="loading" class="text-center py-4">
              <i class="fas fa-spinner fa-spin fa-2x text-secondary"></i>
            </div>

            <div v-else-if="details.length === 0" class="text-center text-secondary py-4">
              Este pedido no tiene productos.
            </div>

            <!-- Listado de productos -->
            <div v-else class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Códigos</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Solicitado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Preparado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="detail in sortedDetails" :key="detail.id" :class="{ 'table-success': isComplete(detail) }">
                    <td>
                      <div class="d-flex px-2 py-1 align-items-center">
                        <img
                          v-if="detail.inventory?.image_path && !brokenImages[detail.id]"
                          :src="imageUrl(detail.inventory.image_path)"
                          class="avatar avatar-sm me-3 border-radius-lg"
                          :alt="productName(detail)"
                          @error="brokenImages[detail.id] = true"
                        />
                        <div
                          v-else
                          class="avatar avatar-sm me-3 border-radius-lg bg-light d-flex align-items-center justify-content-center"
                        >
                          <i class="fas fa-box-open text-secondary"></i>
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ productName(detail) }}</h6>
                          <span v-if="variantLabel(detail)" class="text-xs text-secondary">{{ variantLabel(detail) }}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="text-xs text-secondary d-block">
                        Ref: {{ detail.inventory?.ref || '—' }}
                      </span>
                      <span class="text-xs text-secondary d-block">
                        CB: {{ detail.inventory?.barcode || '—' }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="font-weight-bold text-sm">{{ detail.quantity }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="font-weight-bold text-sm"
                        :class="isComplete(detail) ? 'text-success' : pickedCount(detail) > 0 ? 'text-warning' : 'text-secondary'"
                      >
                        {{ pickedCount(detail) }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm" :class="statusBadgeClass(detail)">
                        {{ statusBadgeLabel(detail) }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-primary py-1 px-2 mb-0"
                        :disabled="isComplete(detail)"
                        title="Marcar una unidad (equivale a escanear)"
                        @click="scanOne(detail)"
                      >
                        <i class="fas fa-plus me-1"></i> 1
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-success py-1 px-2 mb-0 ms-1"
                        :disabled="isComplete(detail)"
                        title="Marcar todo como preparado"
                        @click="markFull(detail)"
                      >
                        <i class="fas fa-check me-1"></i> Todo
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary py-1 px-2 mb-0 ms-1"
                        :disabled="pickedCount(detail) === 0"
                        title="Reiniciar este producto"
                        @click="resetRow(detail)"
                      >
                        <i class="fas fa-undo"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <shipping-modal
      v-if="showShippingModal"
      :remission="remission"
      @close="showShippingModal = false"
      @generated="onWaybillGenerated"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Swal from 'sweetalert2';
import remissionsService from '@/services/remissions.service';
import remissionsDetailsService from '@/services/remissionsDetails.service';
import shippingService from '@/services/shipping.service';
import ShippingModal from './ShippingModal.vue';
import { resolveCityDane, isRestrictedCity } from '@/utils/daneCities';

export default {
  name: 'RemissionsPicking',
  components: { ShippingModal },
  data() {
    return {
      remissionId: null,
      remission: null,
      details: [],
      loading: true,
      processing: false,
      scanCode: '',
      scanFlash: { type: '', message: '' },
      brokenImages: {},
      audioContext: null,
      showShippingModal: false,
      printingLabel: false,
    };
  },
  computed: {
    ...mapGetters('settings', ['conenviosEnabled']),
    totalUnits() {
      return this.details.reduce((sum, d) => sum + (parseInt(d.quantity, 10) || 0), 0);
    },
    pickedUnits() {
      return this.details.reduce((sum, d) => sum + this.pickedCount(d), 0);
    },
    progressPercent() {
      if (this.totalUnits === 0) {
        return 0;
      }
      return Math.min(100, Math.round((this.pickedUnits / this.totalUnits) * 100));
    },
    allComplete() {
      return this.details.length > 0 && this.details.every((d) => this.isComplete(d));
    },
    sellerLabel() {
      return this.remission?.seller?.name || this.remission?.seller_name || '';
    },
    scanFlashIcon() {
      if (this.scanFlash.type === 'success') return 'fas fa-check-circle';
      if (this.scanFlash.type === 'warning') return 'fas fa-exclamation-triangle';
      return 'fas fa-times-circle';
    },
    // Los pendientes quedan arriba mientras se escanea; los completos bajan.
    sortedDetails() {
      return [...this.details].sort((a, b) => this.pendingCount(a) === this.pendingCount(b)
        ? a.id - b.id
        : this.pendingCount(b) - this.pendingCount(a));
    },
    // Id de la guía ConEnvios guardada en picking_json (id o data.id)
    waybillId() {
      const pj = this.remission?.picking_json;
      if (!pj) return null;
      return pj.id ?? pj.data?.id ?? null;
    },
    // Dirección de envío del pedido en LBP
    shippingAddress() {
      const r = this.remission || {};
      return {
        address1: r.address,
        city: r.municipio,
        province: r.departamento,
        province_code: '',
        zip: r.codigo_postal,
      };
    },
  },
  async mounted() {
    this.remissionId = this.$route.query.id || null;
    if (!this.remissionId) {
      await Swal.fire({ icon: 'error', title: 'Error', text: 'No se indicó el pedido a preparar.' });
      this.goBack();
      return;
    }
    await this.loadData();
    this.$store.dispatch('settings/get').catch((e) => console.error('Error cargando settings:', e));
    this.focusScanner();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [remissionRes, detailsRes] = await Promise.all([
          remissionsService.getData(this.remissionId),
          remissionsDetailsService.getData(this.remissionId),
        ]);
        this.remission = remissionRes.data;
        // El detalle puede venir en la remisión o en el endpoint de detalles; priorizamos este último.
        this.details = Array.isArray(detailsRes.data) ? detailsRes.data : (detailsRes.data?.data || remissionRes.data?.details || []);
      } catch (error) {
        console.error('Error al cargar el pedido para picking:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.message || 'No se pudo cargar el pedido.',
        });
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    normalizeCode(value) {
      return String(value ?? '').trim().toLowerCase();
    },
    matchesCode(detail, code) {
      const inventory = detail.inventory || {};
      return this.normalizeCode(inventory.barcode) === code || this.normalizeCode(inventory.ref) === code;
    },
    findDetailByCode(code) {
      const candidates = this.details.filter((d) => this.matchesCode(d, code));
      if (candidates.length === 0) {
        return null;
      }
      return candidates.find((d) => !this.isComplete(d)) || candidates[0];
    },
    async processScan() {
      const code = this.normalizeCode(this.scanCode);
      if (!code || this.loading || this.processing) {
        return;
      }

      const detail = this.findDetailByCode(code);
      if (!detail) {
        this.beep('error');
        this.setFlash('error', `El código "${this.scanCode.trim()}" no pertenece a este pedido.`);
        this.scanCode = '';
        this.focusScanner();
        return;
      }

      if (this.isComplete(detail)) {
        this.beep('warning');
        this.setFlash('warning', `${this.productName(detail)} ya está completamente preparado (${detail.quantity}/${detail.quantity}).`);
        this.scanCode = '';
        this.focusScanner();
        return;
      }

      this.processing = true;
      try {
        const response = await remissionsDetailsService.pick(detail.id);
        const updated = this.replaceDetail(response.data);
        const remaining = this.pendingCount(updated);
        const picked = this.pickedCount(updated);
        if (remaining === 0) {
          this.beep('success');
          this.setFlash('success', `✓ ${this.productName(updated)} completado (${picked}/${updated.quantity}).`);
        } else {
          this.beep('success');
          this.setFlash('success', `✓ ${this.productName(updated)} — faltan ${remaining} de ${updated.quantity}.`);
        }
      } catch (error) {
        console.error('Error al marcar como preparado:', error);
        this.beep('error');
        this.setFlash('error', error?.response?.data?.message || 'No se pudo marcar la unidad como preparada.');
      } finally {
        this.processing = false;
        this.scanCode = '';
        this.focusScanner();
      }
    },
    async scanOne(detail) {
      if (this.isComplete(detail)) {
        return;
      }
      try {
        const response = await remissionsDetailsService.pick(detail.id);
        const updated = this.replaceDetail(response.data);
        const remaining = this.pendingCount(updated);
        this.beep('success');
        this.setFlash('success',
          remaining === 0
            ? `✓ ${this.productName(updated)} completado.`
            : `✓ ${this.productName(updated)} — faltan ${remaining} de ${updated.quantity}.`);
      } catch (error) {
        console.error('Error al marcar como preparado:', error);
        this.beep('error');
        this.setFlash('error', error?.response?.data?.message || 'No se pudo marcar la unidad como preparada.');
      }
    },
    async markFull(detail) {
      try {
        const response = await remissionsDetailsService.pick(detail.id, { quantity: parseInt(detail.quantity, 10) || 0 });
        this.replaceDetail(response.data);
        this.beep('success');
        this.setFlash('success', `✓ ${this.productName(detail)} marcado completamente preparado.`);
      } catch (error) {
        console.error('Error al marcar el producto:', error);
        this.beep('error');
        this.setFlash('error', error?.response?.data?.message || 'No se pudo marcar el producto.');
      }
    },
    async resetRow(detail) {
      try {
        const response = await remissionsDetailsService.pick(detail.id, { quantity: 0 });
        this.replaceDetail(response.data);
        this.setFlash('warning', `${this.productName(detail)} reiniciado a 0.`);
      } catch (error) {
        console.error('Error al reiniciar el producto:', error);
        this.setFlash('error', error?.response?.data?.message || 'No se pudo reiniciar el producto.');
      }
    },
    async resetAll() {
      const result = await Swal.fire({
        title: '¿Reiniciar picking?',
        text: 'Todas las unidades preparadas de este pedido volverán a 0.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, reiniciar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      });
      if (!result.isConfirmed) {
        return;
      }
      try {
        await remissionsDetailsService.resetPicking(this.remissionId);
        await this.loadData();
        this.setFlash('warning', 'Picking reiniciado. Puedes volver a escanear.');
      } catch (error) {
        console.error('Error al reiniciar el picking:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.message || 'No se pudo reiniciar el picking.',
        });
      }
    },
    replaceDetail(updated) {
      if (!updated?.id) {
        return null;
      }
      this.details = this.details.map((d) => (d.id === updated.id ? { ...d, ...updated } : d));
      return updated;
    },
    pickedCount(detail) {
      return parseInt(detail.picked_quantity, 10) || 0;
    },
    pendingCount(detail) {
      return Math.max(0, (parseInt(detail.quantity, 10) || 0) - this.pickedCount(detail));
    },
    isComplete(detail) {
      return detail.quantity > 0 && this.pickedCount(detail) >= parseInt(detail.quantity, 10);
    },
    statusBadgeLabel(detail) {
      if (this.isComplete(detail)) return 'Preparado';
      if (this.pickedCount(detail) > 0) return 'Parcial';
      return 'Pendiente';
    },
    statusBadgeClass(detail) {
      if (this.isComplete(detail)) return 'bg-success';
      if (this.pickedCount(detail) > 0) return 'bg-warning';
      return 'bg-secondary';
    },
    productName(detail) {
      if (detail?.inventory?.name) {
        return detail.inventory.name;
      }
      if (detail?.inventory?.invoice_name) {
        return detail.inventory.invoice_name;
      }
      if (detail?.inventory_id) {
        return `Producto #${detail.inventory_id}`;
      }
      return 'No encontrado';
    },
    variantLabel(detail) {
      const size = detail?.productionModel?.size;
      if (!size) {
        return '';
      }
      const parts = [];
      if (size.size) parts.push(`Talla: ${size.size}`);
      if (size.color?.name) parts.push(`Color: ${size.color.name}`);
      return parts.join(' · ');
    },
    imageUrl(path) {
      const base = String(process.env.VUE_APP_API_IMAGE_URL || '').trim();
      return base + String(path || '').replace(/^\/+/, '').replace(/^storage\//, '');
    },
    setFlash(type, message) {
      this.scanFlash = { type, message };
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => {
        this.scanFlash = { type: '', message: '' };
      }, 5000);
    },
    beep(kind) {
      try {
        this.audioContext = this.audioContext || new (window.AudioContext || window.webkitAudioContext)();
        const ctx = this.audioContext;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = kind === 'error' ? 250 : kind === 'warning' ? 600 : 1000;
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + (kind === 'error' ? 0.35 : 0.15));
      } catch (e) {
        // El audio es un apoyo visual, si falla no debe romper el flujo.
      }
    },
    focusScanner() {
      this.$nextTick(() => this.$refs.scanInput?.focus());
    },
    keepScannerFocus() {
      // Los lectores de código de barras dependen del input enfocado.
      setTimeout(() => {
        if (document.activeElement === document.body) {
          this.focusScanner();
        }
      }, 120);
    },
    normalizeRemissionStatus(status) {
      if (status === 0 || status === '0') return 'pendiente';
      if (status === 1 || status === '1') return 'aceptado';
      if (['pendiente', 'aceptado', 'cancelado', 'devolucion'].includes(status)) return status;
      return 'aceptado';
    },
    remissionStatusLabel(status) {
      const normalized = this.normalizeRemissionStatus(status);
      if (normalized === 'aceptado') return 'Aceptado';
      if (normalized === 'cancelado') return 'Cancelado';
      if (normalized === 'devolucion') return 'Devolución';
      return 'Pendiente';
    },
    remissionStatusClass(status) {
      const normalized = this.normalizeRemissionStatus(status);
      if (normalized === 'aceptado') return 'bg-success';
      if (normalized === 'cancelado' || normalized === 'devolucion') return 'bg-danger';
      return 'bg-warning';
    },
    orderStatusLabel(status) {
      const labels = {
        pendiente: 'Pendiente',
        aceptado: 'Aceptado',
        facturado: 'Facturado',
        enviado: 'Enviado',
        recibido: 'Recibido',
        devolucion: 'Devolución',
      };
      return labels[status || 'pendiente'] || 'Pendiente';
    },
    orderStatusClass(status) {
      const classes = {
        pendiente: 'bg-warning',
        aceptado: 'bg-info',
        facturado: 'bg-primary',
        enviado: 'bg-success',
        recibido: 'bg-dark',
        devolucion: 'bg-danger',
      };
      return classes[status || 'pendiente'] || 'bg-warning';
    },
    goBack() {
      this.$router.push('/remissions').catch(() => {});
    },
    async openShippingModal() {
      // Validación previa: si el destino es la misma ciudad de origen,
      // alertar y NO abrir el modal (defensa en profundidad: también valida el modal).
      const addr = this.shippingAddress || {};
      const dane = resolveCityDane(addr.city, addr.province, addr.province_code, addr.zip);

      if (dane && isRestrictedCity(dane)) {
        await Swal.fire({
          icon: 'warning',
          title: 'Ciudad de destino no permitida',
          text: 'El pedido tiene como destino la misma ciudad de origen del envío (Cúcuta y área metropolitana). No se puede generar la guía.',
          customClass: { container: 'swal-high-z' },
        });
        return;
      }

      this.showShippingModal = true;
    },
    onWaybillGenerated(waybillResponse) {
      // Guardar el response completo de la guía para re-imprimir después
      this.remission = { ...this.remission, picking_json: waybillResponse };
    },
    async printLabel() {
      if (!this.waybillId) return;
      this.printingLabel = true;
      try {
        const res = await shippingService.getWaybillPdf([String(this.waybillId)]);
        const base64 = res.data?.data;
        if (!base64 || typeof base64 !== 'string') {
          throw new Error('La respuesta no contiene el PDF en base64');
        }
        // base64 → Uint8Array → Blob PDF → pestaña nueva
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      } catch (err) {
        console.error('Error al imprimir el rótulo:', err);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'No se pudo obtener el rótulo de la guía.',
          customClass: { container: 'swal-high-z' },
        });
      } finally {
        this.printingLabel = false;
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.flashTimer);
  },
};
</script>
